import { Link } from 'react-router-dom'
import { SEO, FAQSchema } from '@/components/seo'
import { ArrowRight, Aperture, Check } from 'lucide-react'
import { Dropzone } from '@/components/dropzone/Dropzone'
import { buttonStyles } from '@/components/ui'
import { Stats, Testimonials, BeforeAfter } from '@/components/sections'

export function AddNoise() {
  const faqItems = [
    {
      question: 'Why add noise to AI images?',
      answer: 'AI images are often unnaturally clean. Real camera photos have sensor noise, especially in shadows. Adding noise makes images appear more authentic and can help bypass AI detection.',
    },
    {
      question: 'What types of noise can I add?',
      answer: 'We offer camera sensor noise (luminance and chroma), film grain simulation, and subtle pixel perturbation. Each mimics different real-world characteristics.',
    },
    {
      question: 'Will noise reduce image quality?',
      answer: 'When applied correctly, noise adds authenticity without significantly degrading visual quality. We use subtle, realistic amounts calibrated to common camera sensors.',
    },
    {
      question: 'Does this help with AI detection?',
      answer: 'Yes, adding realistic noise patterns can confuse AI classifiers that look for the "too perfect" quality of AI-generated images.',
    },
  ]

  return (
    <>
      <SEO
        title="Add Camera Noise to AI Images - Film Grain Simulator"
        description="Add realistic camera noise and film grain to AI images. Make AI photos look authentic with sensor noise simulation. Free tool."
        canonical="/add-noise"
        keywords="add noise to ai images, camera noise simulation, film grain simulator, ai image noise"
      />
      <FAQSchema questions={faqItems} />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-slate-100 via-white to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 rounded-full text-sm text-slate-700 font-medium mb-6">
              <Aperture className="w-4 h-4" />
              Camera Noise Simulation
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Add Realistic{' '}
              <span className="text-slate-600">Camera Noise</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Add authentic sensor noise and film grain to your AI images.
              Make them look like they came from a real camera.
            </p>
          </div>

          {/* Dropzone */}
          <div className="max-w-2xl mx-auto">
            <Dropzone onFileSelect={() => {}} />
          </div>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Sensor noise
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Film grain
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Adjustable intensity
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Trusted By */}
      <Stats />

      {/* Before/After Examples */}
      <BeforeAfter />

      {/* Testimonials */}
      <Testimonials />

      {/* Noise Types */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Types of Noise We Simulate
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📷 Sensor Noise</h3>
              <p className="text-gray-600 text-sm mb-4">
                Digital camera sensors produce both luminance (brightness) and chroma (color) noise,
                especially visible in shadows and low-light areas.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Luminance noise (grainy texture)</li>
                <li>• Chroma noise (color specks)</li>
                <li>• ISO-calibrated patterns</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎞️ Film Grain</h3>
              <p className="text-gray-600 text-sm mb-4">
                Classic analog film grain for a cinematic or vintage look.
                Simulates the random silver halide crystals in traditional film.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Fine grain (low ISO film)</li>
                <li>• Medium grain (standard film)</li>
                <li>• Heavy grain (high ISO/pushed)</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔬 Micro Perturbation</h3>
              <p className="text-gray-600 text-sm mb-4">
                Subtle pixel-level variations that break the mathematical perfection
                of AI-generated images without visible noise.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Invisible to human eye</li>
                <li>• Breaks AI patterns</li>
                <li>• Preserves image quality</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Noise Matters */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Why Real Photos Have Noise
            </h2>

            <div className="bg-white rounded-xl p-8">
              <p className="text-gray-600 mb-4">
                Every real camera sensor produces some level of noise. This is a fundamental
                characteristic of digital photography that AI models don't naturally replicate.
              </p>

              <h3 className="font-semibold text-gray-900 mt-6 mb-3">Sources of camera noise:</h3>
              <ul className="space-y-2 text-gray-600">
                <li><strong>Shot noise</strong> - Random variation in photon capture</li>
                <li><strong>Read noise</strong> - Electronic interference during sensor readout</li>
                <li><strong>Thermal noise</strong> - Heat-related interference</li>
                <li><strong>Fixed pattern noise</strong> - Sensor manufacturing variations</li>
              </ul>

              <p className="text-gray-600 mt-6">
                AI detectors often look for the absence of these natural characteristics.
                By adding realistic noise, your images become much harder to classify as AI-generated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <details key={index} className="group bg-gray-50 rounded-xl">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-medium text-gray-900">{item.question}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-slate-700">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Add Authentic Noise to Your Images
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Make your AI images look like they came from a real camera.
          </p>
          <Link
            to="/"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-slate-700 hover:bg-gray-100',
            })}
          >
            Start Free
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
